import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ChevronLeft, Users, Clock, Video, DollarSign, Target, FileText,
  ExternalLink, Link as LinkIcon, Play, Pause, Archive, ArrowLeftRight,
  Building2, User, ArrowRight, Repeat2, PenLine, Gift,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { logUpdate } from '@/lib/audit-log';
import { usd } from '@/lib/refunds';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { DataPagination } from '@/components/DataPagination';
import { SampleReceiptBadge } from '@/components/SampleReceiptBadge';
import { fetchSampleReceipts, type SampleReceipt } from '@/lib/sample-receipt';
import {
  ViralRemakeBadge, isViralRemake, briefVideoEntries, remakeDescription,
  VIDEOS_TO_REMAKE_HEADING, WHAT_TO_REMAKE_LABEL,
  MultiProductBadge, isMultiProduct,
  TiktokCollabTypeBadge,
} from '@/components/ViralRemakeBadge';

type CampaignStatus = 'pending' | 'active' | 'paused' | 'archived';

type Campaign = {
  id: string;
  name?: string;
  status: CampaignStatus;
  tap_affiliate_link?: string | null;
  discord_link?: string | null;
  // Optional, brand-reported (2026-09-25) — see TiktokCollabTypeBadge.
  // Admin-visible only; distinct from tap_affiliate_link/tap_campaign_status
  // above, which are BMC ops' own workflow fields for this campaign's actual
  // TAP link, not the brand's self-classification of which model applies.
  tiktok_collab_type?: string | null;
  budget_cap_cents?: number | null;
  budget_spent_cents?: number | null;
  product_id?: string | null;
  product_name?: string | null;
  // Optional brand-entered campaign summary, distinct from product_name —
  // the query selects '*', so no select change was needed.
  description?: string | null;
  created_at: string;
  brand_id?: string | null;
  brand_configs?: { id: string; brand_name: string } | null;
  // Viral Video Remake job — read-only here. The campaign query selects '*', so
  // no select change was needed. Missing/null reads as a standard campaign.
  is_viral_remake?: boolean | null;
  // jsonb array of { url, name?, description? } — description is the per-video
  // remake instruction and is absent on standard/pre-existing campaigns.
  brief_video_urls?: unknown;
  // Multi-product campaigns — embedded via the query's select, so this is a
  // free read, not an extra round trip. Absent/short means single-product.
  cr_campaign_products?: { id: string }[];
  // Creator Bonus (2026-09-29, optional; terms + creator cap 2026-09-30) —
  // the query selects '*', so no select change was needed. bonus_amount_cents/
  // bonus_gmv_threshold_cents both null = no bonus. View-only here; nothing
  // computes whether a hire has earned it or tracks payout.
  bonus_amount_cents?: number | null;
  bonus_gmv_threshold_cents?: number | null;
  bonus_terms?: string | null;
  [key: string]: any;
};

// Full cr_hires.status enum per the DB CHECK constraint
// (cr_hires_status_check, 20260716000005_cr_hires_partial_completed_status.sql)
// — this type previously omitted 'partial_completed' and 'cancelled', which
// made it unreliable as documentation even though nothing here failed at
// compile time (strict/strictNullChecks are off project-wide).
type HireStatus = 'invited' | 'waitlisted' | 'confirmed' | 'declined' | 'expired_offer' | 'completed' | 'failed' | 'cancelled' | 'partial_completed';

type Hire = {
  id: string;
  campaign_id: string;
  brand_id: string;
  creator_id: string;
  package_size: number;
  creator_rate_cents: number;
  brand_rate_cents: number;
  bmc_fee_cents: number;
  // Refunded to the brand's wallet for non-delivery. Subtracted from campaign
  // spend — the money came back, so it is no longer committed here.
  refunded_credit_cents?: number | null;
  status: HireStatus;
  videos_required: number;
  videos_detected: number;
  invited_at: string;
  last_invited_at?: string | null;
  declined_at?: string | null;
  decline_reason?: string | null;
  reinvited_count?: number | null;
  confirmed_at?: string | null;
  completed_at?: string | null;
  // Set when ops closed the hire out short of full delivery (refunded); the
  // engagement is over even though `status` can stay 'confirmed' forever.
  // Fetched via the `*` select below — declared here only so the sample-
  // receipt "live" gate (SampleReceiptBadge's `live` prop) can read it.
  settled_at?: string | null;
  created_at: string;
  source?: 'application' | 'invitation' | null;
  sample_status?: string | null;
  sample_fulfillment_status?: string | null;
  sample_tracking_number?: string | null;
  roster_creators?: {
    name?: string | null;
    email?: string | null;
    tiktok_username?: string | null;
  } | null;
};

type ApplicationPackage = {
  package_size: number;
  proposed_rate_cents: number;
  proposed_rate_with_rights_cents?: number | null;
};

type Application = {
  id: string;
  campaign_id: string;
  brand_id: string;
  creator_id: string;
  packages: ApplicationPackage[];
  note_to_brand?: string | null;
  status: string;
  applied_at: string;
  last_applied_at?: string | null;
  application_rejected_count?: number | null;
  reject_reason?: string | null;
  roster_creators?: {
    name?: string | null;
    email?: string | null;
    tiktok_username?: string | null;
  } | null;
};

// One round of a brand⇄creator rate negotiation on one anchor (an
// application OR a hire), read-only. Fetched once per campaign (below) and
// grouped into a full ordered chain per anchor — the compact table cell shows
// only the latest round, but the "View history" button opens the WHOLE chain,
// so admin can see every round, not just where it currently stands.
type CounterOfferChainRow = {
  id: string;
  application_id: string | null;
  hire_id: string | null;
  sender: 'brand' | 'creator';
  round: number;
  package_size: number;
  creator_rate_cents: number;
  brand_rate_cents: number;
  with_rights: boolean;
  note: string | null;
  status: 'pending' | 'accepted' | 'declined' | 'countered' | 'withdrawn' | 'expired';
  decline_reason: string | null;
  created_at: string;
  responded_at: string | null;
};

type VideoSubmission = {
  id: string;
  tiktok_video_url: string;
  tiktok_video_id: string | null;
  views: number | null;
  gmv_cents: number | null;
  orders: number | null;
  units_sold: number | null;
  submitted_at: string | null;
  roster_creators: { name: string | null; tiktok_username: string | null } | null;
};

type HireTab = 'all' | 'hired' | 'invited' | 'waitlisted' | 'rejected' | 'applications' | 'videos';

function StatusBadge({ status }: { status: string }) {
  if (status === 'active')   return <Badge className="bg-green-100 text-green-800 border border-green-200">Active</Badge>;
  if (status === 'paused')   return <Badge className="bg-orange-100 text-orange-800 border border-orange-200">Paused</Badge>;
  if (status === 'archived') return <Badge className="bg-gray-100 text-gray-600 border border-gray-200">Archived</Badge>;
  return <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200">Pending</Badge>;
}

function HireStatusBadge({ status }: { status: string }) {
  if (status === 'confirmed')         return <Badge className="bg-green-100 text-green-800 border border-green-200">Confirmed</Badge>;
  if (status === 'completed')         return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">Completed</Badge>;
  if (status === 'partial_completed') return <Badge className="bg-amber-100 text-amber-800 border border-amber-200">Partial Completed</Badge>;
  if (status === 'invited')           return <Badge className="bg-blue-100 text-blue-800 border border-blue-200">Invited</Badge>;
  if (status === 'waitlisted')    return <Badge className="bg-purple-100 text-purple-800 border border-purple-200">Waitlisted</Badge>;
  if (status === 'declined')      return <Badge className="bg-red-100 text-red-700 border border-red-200">Declined</Badge>;
  if (status === 'expired_offer') return <Badge className="bg-gray-100 text-gray-600 border border-gray-200">Expired</Badge>;
  if (status === 'failed')        return <Badge className="bg-orange-100 text-orange-800 border border-orange-200">Failed</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

// Read-only — admin isn't a party to the negotiation, this is visibility
// into it. Shows just the LATEST round as a compact, clickable summary;
// "View all" opens the full chain (see NegotiationHistoryDialog) rather than
// cramming every round into the table cell. 'pending' is the only status
// worth a strong color — it's the one that means someone is waiting on the
// other side right now. 'accepted'/'countered' latest rounds are skipped in
// the summary (the row's own status already tells that story) but still
// count toward "has a history" for the button.
function NegotiationBadge({ chain, onOpen }: { chain: CounterOfferChainRow[]; onOpen: () => void }) {
  if (chain.length === 0) {
    return <span className="text-muted-foreground text-xs italic">—</span>;
  }
  const latest = chain[chain.length - 1];
  const rate = `$${(latest.brand_rate_cents / 100).toLocaleString()}`;
  const summary =
    latest.status === 'accepted' || latest.status === 'countered'
      ? null
      : latest.status === 'pending'
      ? <Badge className="bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-200">
          Round {latest.round} · {latest.sender === 'brand' ? 'Brand' : 'Creator'} asked {rate}
        </Badge>
      : <span className="text-muted-foreground">
          Round {latest.round} {latest.status === 'declined' ? 'declined' : latest.status === 'withdrawn' ? 'withdrawn' : 'expired'}
        </span>;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex flex-col items-start gap-1 text-xs hover:opacity-80 transition-opacity"
      title="View full negotiation history"
    >
      {summary}
      <span className="text-[11px] text-blue-600 underline underline-offset-2">
        View {chain.length === 1 ? 'offer' : `all ${chain.length} rounds`}
      </span>
    </button>
  );
}

// Visual identity for "who sent this round". Solid-filled (not a pale tint)
// so the timeline dot reads as a strong color chip even at a glance — this is
// the PRIMARY way to tell brand vs. creator apart, decoupled from the status
// color on the card itself, so the two dimensions ("who" vs. "what happened")
// never fight for the same color.
function negotiationSenderMeta(sender: 'brand' | 'creator') {
  return sender === 'brand'
    ? { label: 'Brand', Icon: Building2, dot: 'bg-blue-600', name: 'text-blue-700' }
    : { label: 'Creator', Icon: User, dot: 'bg-violet-600', name: 'text-violet-700' };
}

function negotiationStatusBadge(status: CounterOfferChainRow['status']) {
  if (status === 'pending')   return <Badge className="bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-200">Awaiting response</Badge>;
  if (status === 'accepted')  return <Badge className="bg-green-100 text-green-800 border border-green-200">Accepted</Badge>;
  if (status === 'declined')  return <Badge className="bg-red-100 text-red-700 border border-red-200">Declined</Badge>;
  if (status === 'countered') return <Badge className="bg-slate-100 text-slate-500 border border-slate-200">Superseded</Badge>;
  if (status === 'withdrawn') return <Badge className="bg-slate-100 text-slate-500 border border-slate-200">Withdrawn</Badge>;
  return <Badge className="bg-slate-100 text-slate-500 border border-slate-200">Expired</Badge>;
}

// Card styling per round status — 'pending' is the only round anyone is
// actually waiting on right now, so it gets the strongest treatment (tint +
// ring + a touch of elevation). Accepted/declined get a light tint of their
// own color so the outcome reads at a glance. Superseded/withdrawn/expired
// rounds are just history — flattened to a plain card so they recede behind
// whatever round actually matters.
function negotiationCardClass(status: CounterOfferChainRow['status']) {
  if (status === 'pending')   return 'border-fuchsia-300 bg-fuchsia-50/80 ring-1 ring-fuchsia-200 shadow-sm';
  if (status === 'accepted')  return 'border-green-200 bg-green-50/60';
  if (status === 'declined')  return 'border-red-200 bg-red-50/50';
  return 'border-slate-200 bg-slate-50/50 opacity-75';
}

// The full, ordered chain for one application or hire — every round, both
// sides, read-only. Opened from NegotiationBadge's "View all rounds" link.
// Rendered as a timeline (colored dot per sender, connected by a spine) —
// the dot is what actually answers "who sent this", separate from the card's
// own status color, so the two questions ("who" and "what happened") read
// independently instead of both fighting over one color.
function NegotiationHistoryDialog({ state, onClose }: {
  state: { creatorName: string; brandName: string; kind: 'application' | 'invite'; chain: CounterOfferChainRow[] } | null;
  onClose: () => void;
}) {
  const chain = state?.chain ?? [];
  return (
    <Dialog open={!!state} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Negotiation history</DialogTitle>
          {/* asChild swaps Radix's default <p> for a <div> — we need a flex
              row of chips here, which a <p> can't validly contain. */}
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 pt-0.5 text-sm">
              <span className="inline-flex items-center gap-1 font-medium text-violet-700">
                <User className="h-3 w-3" />
                {state?.creatorName}
              </span>
              <span className="text-muted-foreground/60">↔</span>
              <span className="inline-flex items-center gap-1 font-medium text-blue-700">
                <Building2 className="h-3 w-3" />
                {state?.brandName}
              </span>
              <span className="text-muted-foreground">
                · {state?.kind === 'invite' ? 'Invite' : 'Application'}
                {chain.length > 0 && ` · ${chain.length} round${chain.length === 1 ? '' : 's'}`}
              </span>
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="relative max-h-[60vh] overflow-y-auto py-1 pr-1">
          {chain.length > 0 && (
            <div className="absolute left-[19px] top-3 bottom-3 w-px bg-border" aria-hidden="true" />
          )}
          <div className="space-y-3">
            {chain.map(c => {
              const sender = negotiationSenderMeta(c.sender);
              const feeCents = c.brand_rate_cents - c.creator_rate_cents;
              return (
                <div key={c.id} className="relative flex gap-3">
                  <span className={`relative z-10 mt-0.5 flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full text-white shadow-sm ring-4 ring-background ${sender.dot}`}>
                    <sender.Icon className="h-4 w-4" />
                  </span>
                  <div className={`min-w-0 flex-1 space-y-2 rounded-xl border p-3.5 ${negotiationCardClass(c.status)}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-baseline gap-1.5">
                        <span className={`text-sm font-bold ${sender.name}`}>{sender.label}</span>
                        <span className="text-xs text-muted-foreground">offered · Round {c.round}</span>
                      </div>
                      {negotiationStatusBadge(c.status)}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <div>
                        <div className="text-lg font-bold tabular-nums leading-tight">
                          ${(c.brand_rate_cents / 100).toLocaleString()}
                        </div>
                        <div className="text-[11px] text-muted-foreground leading-tight">brand pays</div>
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />
                      <div>
                        <div className="text-lg font-semibold tabular-nums leading-tight text-foreground/80">
                          ${(c.creator_rate_cents / 100).toLocaleString()}
                        </div>
                        <div className="text-[11px] text-muted-foreground leading-tight">creator gets</div>
                      </div>
                    </div>

                    <div className="text-xs text-muted-foreground">
                      {c.package_size} video{c.package_size === 1 ? '' : 's'}{c.with_rights ? ' · usage rights' : ''}
                      {feeCents > 0 && ` · $${(feeCents / 100).toLocaleString()} platform fee`}
                    </div>

                    {c.note && <p className="text-sm text-muted-foreground italic">"{c.note}"</p>}
                    {c.decline_reason && <p className="text-sm text-red-600 italic">"{c.decline_reason}"</p>}

                    <p className="text-xs text-muted-foreground">
                      Sent {format(new Date(c.created_at), 'MMM d, yyyy · h:mm a')}
                      {c.responded_at && ` · Responded ${format(new Date(c.responded_at), 'MMM d, yyyy · h:mm a')}`}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          {chain.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">No counter offers.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SampleStatusBadge({ status }: { status?: string | null }) {
  if (!status) return <span className="text-muted-foreground text-xs italic">Not requested</span>;
  const s = status.toUpperCase();
  if (s === 'APPROVED')                        return <Badge className="bg-green-100 text-green-800 border border-green-200">Approved</Badge>;
  if (s === 'PENDING')                         return <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200">Pending</Badge>;
  if (s === 'REJECTED' || s === 'DISAPPROVED') return <Badge className="bg-red-100 text-red-700 border border-red-200">Rejected</Badge>;
  if (s === 'EXPIRED')                         return <Badge className="bg-gray-100 text-gray-600 border border-gray-200">Expired</Badge>;
  if (s === 'SHIPPED')                         return <Badge className="bg-blue-100 text-blue-800 border border-blue-200">Shipped</Badge>;
  if (s.includes('CANCEL'))                    return <Badge className="bg-slate-100 text-slate-600 border border-slate-200">Cancelled</Badge>;
  return <Badge variant="outline" className="capitalize">{status.toLowerCase()}</Badge>;
}

function StatCard({ icon: Icon, label, value, sub, valueClass = '' }: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  sub?: string;
  valueClass?: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4 space-y-2">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function isValidUrl(value: string) {
  if (!value.trim()) return true;
  try {
    const url = new URL(value.trim().includes('://') ? value.trim() : `https://${value.trim()}`);
    return url.hostname.includes('.');
  } catch {
    return false;
  }
}

// Supabase caps a single response at 1000 rows — page through with .range()
// so campaigns with >1000 hires still get accurate stats (budget spent,
// hired/invited/waitlisted counts, etc.) instead of silently truncating.
async function fetchAllPages<T>(
  buildQuery: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: any }>
): Promise<T[]> {
  const PAGE_SIZE = 1000;
  const all: T[] = [];
  let from = 0;
  while (true) {
    const { data, error } = await buildQuery(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    all.push(...(data ?? []));
    if (!data || data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return all;
}

function creatorName(h: Hire) { return h.roster_creators?.name ?? '—'; }
function creatorHandle(h: Hire) { return h.roster_creators?.tiktok_username ?? null; }
function creatorEmail(h: Hire) { return h.roster_creators?.email ?? null; }

export default function RosterCampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [hireTab, setHireTab] = useState<HireTab>('hired');
  const [appSubTab, setAppSubTab] = useState<'pending' | 'approved' | 'rejected' | 'completed' | 'withdrawn'>('pending');

  // Videos tab — split into raw input and debounced value used in queries
  const [videoSearchInput, setVideoSearchInput] = useState('');
  const [videoDebouncedSearch, setVideoDebouncedSearch] = useState('');
  const [videoPage, setVideoPage] = useState(1);
  const [videoPageSize, setVideoPageSize] = useState(10);

  // Hires + Applications tabs — shared search
  const [hireSearchInput, setHireSearchInput] = useState('');
  const [hireDebouncedSearch, setHireDebouncedSearch] = useState('');

  const [editTapOpen, setEditTapOpen] = useState(false);
  const [tapLinkInput, setTapLinkInput] = useState('');
  const [editBudgetOpen, setEditBudgetOpen] = useState(false);
  const [budgetInput, setBudgetInput] = useState('');
  const [statusConfirm, setStatusConfirm] = useState<CampaignStatus | null>(null);

  // Debounce video search → 400ms
  useEffect(() => {
    const t = setTimeout(() => setVideoDebouncedSearch(videoSearchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [videoSearchInput]);

  // Reset to page 1 whenever the video search term changes
  useEffect(() => { setVideoPage(1); }, [videoDebouncedSearch]);

  // Debounce hire/application search → 400ms
  useEffect(() => {
    const t = setTimeout(() => setHireDebouncedSearch(hireSearchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [hireSearchInput]);

  const { data: campaign, isLoading: campaignLoading } = useQuery<Campaign>({
    queryKey: ['roster-campaign-detail', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cr_campaigns')
        .select('*, brand_configs(id, brand_name), cr_campaign_products(id)')
        .eq('id', id)
        .limit(1);
      if (error) throw error;
      if (!data?.[0]) throw new Error('Campaign not found');
      return data[0] as Campaign;
    },
  });

  const { data: hires = [], isLoading: hiresLoading } = useQuery<Hire[]>({
    queryKey: ['roster-campaign-hires', id, hireDebouncedSearch],
    enabled: !!id,
    queryFn: async () => {
      let creatorIds: string[] | null = null;

      if (hireDebouncedSearch) {
        const q = hireDebouncedSearch;
        const matching = await fetchAllPages<{ creator_id: string }>((from, to) =>
          supabase
            .from('cr_hires')
            .select('creator_id, roster_creators!inner(id)')
            .eq('campaign_id', id)
            .not('creator_id', 'is', null)
            .or(`name.ilike.%${q}%,tiktok_username.ilike.%${q}%,email.ilike.%${q}%`, { referencedTable: 'roster_creators' })
            .range(from, to)
        );

        creatorIds = [...new Set(matching.map(h => h.creator_id).filter(Boolean))];
        if (creatorIds.length === 0) return [];
      }

      try {
        return await fetchAllPages<Hire>((from, to) => {
          let query = supabase
            .from('cr_hires')
            .select('*, roster_creators(name, email, tiktok_username)')
            .eq('campaign_id', id);
          if (creatorIds) query = query.in('creator_id', creatorIds);
          return query.range(from, to);
        });
      } catch (error: any) {
        if (error.code === '42P01') return [];
        throw error;
      }
    },
  });

  const { data: applications = [], isLoading: applicationsLoading } = useQuery<Application[]>({
    queryKey: ['roster-campaign-applications', id, hireDebouncedSearch],
    enabled: !!id,
    queryFn: async () => {
      let query = supabase
        .from('cr_applications')
        .select('*, roster_creators(name, email, tiktok_username)')
        .eq('campaign_id', id);

      if (hireDebouncedSearch) {
        const q = hireDebouncedSearch;
        const { data: matching } = await supabase
          .from('cr_applications')
          .select('creator_id, roster_creators!inner(id)')
          .eq('campaign_id', id)
          .not('creator_id', 'is', null)
          .or(`name.ilike.%${q}%,tiktok_username.ilike.%${q}%,email.ilike.%${q}%`, { referencedTable: 'roster_creators' });

        const creatorIds = [...new Set(
          (matching ?? []).map((a: any) => a.creator_id).filter(Boolean)
        )];
        if (creatorIds.length === 0) return [];
        query = query.in('creator_id', creatorIds);
      }

      const { data, error } = await query;
      if (error) {
        if (error.code === '42P01') return [];
        throw error;
      }
      return (data ?? []) as Application[];
    },
  });

  // Every counter-offer round for every hire/application in this campaign,
  // keyed by anchor id. One query for the whole page rather than per-row —
  // this table can hold hundreds of applications, and a counter is the
  // exception, not the norm, so most rows never look anything up. Full
  // chains, not just the latest round: the table cell shows a compact summary,
  // but the "View all rounds" button needs every round, and refetching per
  // click would mean a request per open instead of one for the whole page.
  const { data: counterOffers = [] } = useQuery<CounterOfferChainRow[]>({
    queryKey: ['roster-campaign-counter-offers', id],
    enabled: !!id,
    queryFn: async () => {
      try {
        return await fetchAllPages<CounterOfferChainRow>((from, to) =>
          supabase
            .from('cr_counter_offers')
            .select('id, application_id, hire_id, sender, round, package_size, creator_rate_cents, brand_rate_cents, with_rights, note, status, decline_reason, created_at, responded_at')
            .eq('campaign_id', id)
            .order('created_at', { ascending: true })
            .range(from, to)
        );
      } catch (error: any) {
        if (error.code === '42P01') return [];
        throw error;
      }
    },
  });

  // Grouped in created_at-ascending order (the query above is already sorted
  // that way), so each array is the chain oldest-first and its last element
  // is always the latest round — exactly what NegotiationBadge's compact
  // summary and the "latest" checks below need, with no extra sort.
  const chainByApplication = new Map<string, CounterOfferChainRow[]>();
  const chainByHire = new Map<string, CounterOfferChainRow[]>();
  for (const c of counterOffers) {
    if (c.application_id) {
      const arr = chainByApplication.get(c.application_id) ?? [];
      arr.push(c);
      chainByApplication.set(c.application_id, arr);
    } else if (c.hire_id) {
      const arr = chainByHire.get(c.hire_id) ?? [];
      arr.push(c);
      chainByHire.set(c.hire_id, arr);
    }
  }

  const [negotiationDialog, setNegotiationDialog] = useState<{ creatorName: string; brandName: string; kind: 'application' | 'invite'; chain: CounterOfferChainRow[] } | null>(null);

  // Aggregate stats — DB-level totals, always accurate regardless of page or search
  const { data: videoStats } = useQuery({
    queryKey: ['roster-campaign-video-stats', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_cr_video_stats', { p_campaign_id: id });
      if (error) throw error;
      const row = (data as any[])?.[0];
      return {
        total: Number(row?.total_count ?? 0),
        totalGmvCents: Number(row?.total_gmv_cents ?? 0),
        totalViews: Number(row?.total_views ?? 0),
      };
    },
  });

  // Per-hire delivered-video counts, for the "Videos" column in the hires
  // table. NOT read from cr_hires.videos_detected — that column is dead: it
  // was only ever populated by cronCompletionCheck (Brand-Dashboard's
  // worker/src/hiring.ts), whose cron dispatch has been commented out since,
  // so it's permanently stuck at 0 for every hire. cr_video_submissions is
  // the live source of truth (Brand-Dashboard's HiredCreators.tsx already
  // made this same switch for the brand-facing view). Rejected submissions
  // don't count — the creator is expected to replace them — matching the
  // exclusion used everywhere else (get_cr_video_stats, the videos listing above).
  const { data: videosByHire = {} } = useQuery<Record<string, number>>({
    queryKey: ['roster-campaign-videos-by-hire', id],
    enabled: !!id,
    queryFn: async () => {
      const rows = await fetchAllPages<{ hire_id: string; review_status: string | null }>((from, to) =>
        supabase
          .from('cr_video_submissions')
          .select('hire_id, review_status')
          .eq('campaign_id', id)
          .eq('deleted', false)
          .range(from, to)
      );
      const counts: Record<string, number> = {};
      for (const r of rows) {
        if (r.review_status === 'rejected') continue;
        counts[r.hire_id] = (counts[r.hire_id] ?? 0) + 1;
      }
      return counts;
    },
  });

  // Per-hire count of videos TikTok no longer serves. Deliberately a separate
  // map from videosByHire above rather than a branch inside it: these must
  // never be added to the delivered count, and keeping the two apart makes that
  // impossible to get wrong later. Shown under the count purely as context —
  // "4 / 5 with one removed" is a different situation from "only four were ever
  // uploaded", and it is the reason a hire can go back to confirmed on its own.
  const { data: deletedByHire = {} } = useQuery<Record<string, number>>({
    queryKey: ['roster-campaign-deleted-by-hire', id],
    enabled: !!id,
    queryFn: async () => {
      const rows = await fetchAllPages<{ hire_id: string }>((from, to) =>
        supabase
          .from('cr_video_submissions')
          .select('hire_id')
          .eq('campaign_id', id)
          .eq('deleted', true)
          .range(from, to)
      );
      const counts: Record<string, number> = {};
      for (const r of rows) counts[r.hire_id] = (counts[r.hire_id] ?? 0) + 1;
      return counts;
    },
  });

  // Creator's own "did your sample arrive?" answer per hire. Separate,
  // fail-soft query — see lib/sample-receipt.
  const { data: receiptsByHire = {} } = useQuery<Record<string, SampleReceipt>>({
    queryKey: ['roster-campaign-sample-receipts', id, hires.map(h => h.id).join(',')],
    enabled: !!id && hires.length > 0,
    queryFn: () => fetchSampleReceipts(hires.map(h => h.id)),
  });

  // Paginated + DB-searched videos
  const { data: videosPage, isLoading: videosLoading } = useQuery({
    queryKey: ['roster-campaign-videos', id, videoPage, videoPageSize, videoDebouncedSearch],
    enabled: !!id,
    queryFn: async () => {
      const from = (videoPage - 1) * videoPageSize;
      const to = from + videoPageSize - 1;
      const q = videoDebouncedSearch;

      // review_status IS NULL means "no decision made" and counts as approved
      // (same convention as MyVideos.tsx / Home.tsx in Brand-Dashboard) — so
      // rejected videos are excluded without also dropping every undecided one.
      let query = supabase
        .from('cr_video_submissions')
        .select(
          'id, tiktok_video_url, tiktok_video_id, views, gmv_cents, orders, units_sold, submitted_at, roster_creators(name, tiktok_username)',
          { count: 'exact' }
        )
        .eq('campaign_id', id)
        .eq('deleted', false)
        .or('review_status.is.null,review_status.neq.rejected')
        .order('submitted_at', { ascending: false })
        .range(from, to);

      if (q) {
        // Resolve creator IDs scoped to this campaign's submissions only
        const { data: matchingSubmissions } = await supabase
          .from('cr_video_submissions')
          .select('creator_id, roster_creators!inner(id)')
          .eq('campaign_id', id)
          .eq('deleted', false)
          .or('review_status.is.null,review_status.neq.rejected')
          .not('creator_id', 'is', null)
          .or(`name.ilike.%${q}%,tiktok_username.ilike.%${q}%`, { referencedTable: 'roster_creators' });

        const creatorIds = [...new Set(
          (matchingSubmissions ?? []).map((c: any) => c.creator_id).filter(Boolean)
        )];
        const orParts = [
          `tiktok_video_id.ilike.%${q}%`,
          `tiktok_video_url.ilike.%${q}%`,
        ];
        if (creatorIds.length > 0) {
          orParts.push(`creator_id.in.(${creatorIds.join(',')})`);
        }
        query = query.or(orParts.join(','));
      }

      const { data, error, count } = await query;
      if (error) throw error;
      return { rows: (data ?? []) as VideoSubmission[], filteredCount: count ?? 0 };
    },
  });

  const saveTapMutation = useMutation({
    mutationFn: async (tapLink: string) => {
      const { error } = await supabase
        .from('cr_campaigns')
        .update({ tap_affiliate_link: tapLink || null })
        .eq('id', id);
      if (error) throw error;
      await logUpdate('cr_campaigns', id!, { tap_affiliate_link: campaign?.tap_affiliate_link }, { tap_affiliate_link: tapLink || null }, { description: 'Admin updated TAP affiliate link' });
    },
    onSuccess: () => {
      toast.success('TAP link saved');
      setEditTapOpen(false);
      setTapLinkInput('');
      queryClient.invalidateQueries({ queryKey: ['roster-campaign-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['roster-campaigns'] });
    },
    onError: (err: any) => toast.error(err.message || 'Save failed'),
  });

  const saveBudgetMutation = useMutation({
    mutationFn: async (cents: number) => {
      const { error } = await supabase
        .from('cr_campaigns')
        .update({ budget_cap_cents: cents })
        .eq('id', id);
      if (error) throw error;
      await logUpdate('cr_campaigns', id!, { budget_cap_cents: campaign?.budget_cap_cents }, { budget_cap_cents: cents }, { description: 'Admin updated budget cap' });
    },
    onSuccess: () => {
      toast.success('Budget cap saved');
      setEditBudgetOpen(false);
      setBudgetInput('');
      queryClient.invalidateQueries({ queryKey: ['roster-campaign-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['roster-campaigns'] });
    },
    onError: (err: any) => toast.error(err.message || 'Save failed'),
  });

  const statusMutation = useMutation({
    mutationFn: async (status: CampaignStatus) => {
      if (status === 'active' && !campaign?.tap_affiliate_link) throw new Error('Add a TAP affiliate link before activating.');
      const { error } = await supabase.from('cr_campaigns').update({ status }).eq('id', id);
      if (error) throw error;
      await logUpdate('cr_campaigns', id!, { status: campaign?.status }, { status }, { description: `Admin changed campaign status to ${status}` });
    },
    onSuccess: (_, status) => {
      const labels: Record<CampaignStatus, string> = { active: 'Campaign activated', paused: 'Campaign paused', archived: 'Campaign archived', pending: 'Campaign set to pending' };
      toast.success(labels[status]);
      setStatusConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['roster-campaign-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['roster-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['roster-campaigns-counts'] });
    },
    onError: (err: any) => toast.error(err.message || 'Status update failed'),
  });

  if (campaignLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}
        </div>
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground gap-4">
        <p>Campaign not found.</p>
        <Button variant="link" onClick={() => navigate('/roster-campaigns')}>Return to Roster Jobs</Button>
      </div>
    );
  }

  const campaignName = campaign.name || campaign.id;

  const hiredCount      = hires.filter(h => h.status === 'confirmed' || h.status === 'completed' || h.status === 'partial_completed').length;
  const invitedCount    = hires.filter(h => h.status === 'invited').length;
  const waitlistedCount = hires.filter(h => h.status === 'waitlisted').length;
  // videoStats.total is get_cr_video_stats' live count of non-deleted,
  // non-rejected cr_video_submissions for the campaign — the same number
  // the "Videos" tab header shows. cr_hires.videos_detected (summed here
  // previously) is a dead column; see the videosByHire query above.
  const videosDetected  = videoStats?.total ?? 0;
  const videosRequired  = hires
    .filter(h => h.status === 'confirmed' || h.status === 'completed' || h.status === 'partial_completed')
    .reduce((s, h) => s + (h.videos_required ?? 0), 0);

  // Spend vs budget, net of refunds. A hire refunded for non-delivery hands the
  // money back to the brand as wallet credit, so it is no longer committed to
  // this campaign — counting it as spend overstates the burn and understates
  // what's left to hire with. Refunds are summed over the SAME hires whose gross
  // we counted, so the two can never disagree about which rows are in scope.
  // (cr_hires.refunded_credit_cents is written by wallet_refund_hire_to_credit;
  // note the campaign's own budget_spent_cents column is NOT decremented on
  // refund, which is why this page derives spend from the hires instead.)
  const budgetCapCents  = campaign.budget_cap_cents ?? 0;
  const paidHires       = hires.filter(h => h.status === 'confirmed' || h.status === 'completed' || h.status === 'partial_completed');
  const grossSpentCents = paidHires.reduce((s, h) => s + (h.brand_rate_cents ?? 0), 0);
  const refundedCents   = paidHires.reduce((s, h) => s + (h.refunded_credit_cents ?? 0), 0);
  const netSpentCents   = Math.max(0, grossSpentCents - refundedCents);
  const budgetCap       = budgetCapCents / 100;
  const budgetPct       = budgetCapCents > 0 ? Math.round((netSpentCents / budgetCapCents) * 100) : 0;

  const rejectedCount = hires.filter(h => h.status === 'declined' || h.status === 'expired_offer').length;

  const tabRows: Record<Exclude<HireTab, 'applications' | 'videos'>, Hire[]> = {
    all:        hires,
    hired:      hires.filter(h => h.status === 'confirmed' || h.status === 'completed' || h.status === 'partial_completed'),
    invited:    hires.filter(h => h.status === 'invited'),
    waitlisted: hires.filter(h => h.status === 'waitlisted'),
    rejected:   hires.filter(h => h.status === 'declined' || h.status === 'expired_offer'),
  };
  const activeHireRows = hireTab !== 'applications' && hireTab !== 'videos' ? tabRows[hireTab] : [];
  const allAppRows     = hireTab === 'applications' ? applications : [];
  const activeAppRows  = allAppRows.filter(a => a.status === appSubTab);

  const appSubCounts = {
    pending:   allAppRows.filter(a => a.status === 'pending').length,
    approved:  allAppRows.filter(a => a.status === 'approved').length,
    rejected:  allAppRows.filter(a => a.status === 'rejected').length,
    completed: allAppRows.filter(a => a.status === 'completed').length,
    withdrawn: allAppRows.filter(a => a.status === 'withdrawn').length,
  };

  const totalVideoPages = Math.ceil(
    (videoDebouncedSearch ? (videosPage?.filteredCount ?? 0) : (videoStats?.total ?? 0)) / videoPageSize
  );

  return (
    <div className="space-y-6">
      {/* Back nav */}
      <div>
        <Button variant="ghost" size="sm" asChild className="text-muted-foreground -ml-2">
          <Link to="/roster-campaigns">
            <ChevronLeft className="h-4 w-4 mr-1" />
            All campaigns
          </Link>
        </Button>
      </div>

      {/* Campaign header */}
      <div className="space-y-2">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight break-all">{campaignName}</h1>
        <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
          {campaign.brand_configs?.brand_name && <span>{campaign.brand_configs.brand_name}</span>}
          {isMultiProduct(campaign) ? (
            <><span>·</span><span>{campaign.cr_campaign_products!.length} products</span></>
          ) : (
            <>
              {campaign.product_id && <><span>·</span><span>Product ID {campaign.product_id}</span></>}
              {campaign.product_name && <><span>·</span><span>{campaign.product_name}</span></>}
            </>
          )}
          {budgetCap > 0 && <><span>·</span><span>Budget cap {usd(budgetCapCents)}</span></>}
          {!!campaign.bonus_amount_cents && !!campaign.bonus_gmv_threshold_cents && (
            <><span>·</span><span>Bonus {usd(campaign.bonus_amount_cents)} at {usd(campaign.bonus_gmv_threshold_cents)} GMV</span></>
          )}
          <span>·</span>
          <StatusBadge status={campaign.status} />
          {isViralRemake(campaign) && <ViralRemakeBadge />}
          {isMultiProduct(campaign) && <MultiProductBadge count={campaign.cr_campaign_products!.length} />}
          <TiktokCollabTypeBadge value={campaign.tiktok_collab_type} />
        </div>
        {campaign.description && (
          <p className="text-sm text-foreground/80 max-w-2xl whitespace-pre-wrap">{campaign.description}</p>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setEditBudgetOpen(true); setBudgetInput(String(Math.round(budgetCap))); }}
          >
            Edit budget cap
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setEditTapOpen(true); setTapLinkInput(campaign.tap_affiliate_link ?? ''); }}>
            <LinkIcon className="h-3.5 w-3.5 mr-1.5" />
            {campaign.tap_affiliate_link ? 'Edit TAP Link' : 'Add TAP Link'}
          </Button>
          {campaign.tap_affiliate_link && (
            <Button size="sm" variant="ghost" asChild>
              <a href={campaign.tap_affiliate_link} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                View TAP Link
              </a>
            </Button>
          )}
          {campaign.discord_link && (
            <Button size="sm" variant="ghost" asChild>
              <a href={campaign.discord_link} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                View Discord Link
              </a>
            </Button>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard icon={Users} label="Hired Creators" value={hiredCount} />
        <StatCard
          icon={Clock}
          label="Invites Pending"
          value={invitedCount === 0 ? '—' : invitedCount}
          sub={invitedCount === 0 ? 'no open invites' : undefined}
        />
        <StatCard
          icon={Video}
          label="Videos Delivered"
          value={videosDetected}
          sub={`${videosRequired} expected`}
        />
        <StatCard
          icon={FileText}
          label="Applications"
          value={applications.length === 0 ? '—' : applications.length}
          sub={applications.length === 0 ? 'no applications yet' : undefined}
        />
        <StatCard
          icon={DollarSign}
          label="Waitlisted"
          value={waitlistedCount === 0 ? '—' : waitlistedCount}
          sub={waitlistedCount === 0 ? 'none pending approval' : 'pending approval'}
        />
        <StatCard
          icon={Target}
          label="Spend vs Budget"
          value={`${usd(netSpentCents)} / ${usd(budgetCapCents)}`}
          // Name the refund when there is one. Without it a card reading
          // "$382.70 / $1,000" looks like an arithmetic error next to the hires
          // below, which still show their full rates.
          sub={refundedCents > 0
            ? `${budgetPct}% of budget · ${usd(refundedCents)} refunded`
            : `${budgetPct}% of budget`}
        />
      </div>

      {/* Status actions */}
      <div className="flex flex-wrap gap-2">
        {campaign.status !== 'active' && (
          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setStatusConfirm('active')}>
            <Play className="h-3.5 w-3.5 mr-1.5" /> Activate
          </Button>
        )}
        {campaign.status === 'active' && (
          <Button size="sm" variant="outline" className="text-orange-600 border-orange-200 hover:bg-orange-50" onClick={() => setStatusConfirm('paused')}>
            <Pause className="h-3.5 w-3.5 mr-1.5" /> Pause
          </Button>
        )}
        {campaign.status !== 'archived' && (
          <Button size="sm" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => setStatusConfirm('archived')}>
            <Archive className="h-3.5 w-3.5 mr-1.5" /> Archive
          </Button>
        )}
      </div>

      {/* Creator Bonus — dedicated section (not just the compact "Bonus $X at
          $Y GMV" mention in the header meta row above) so the GMV threshold,
          the optional creator cap, and the brand-written terms are readable
          without hovering anything. Gated the same way as the header mention
          — both amount and threshold set. Read-only: nothing here computes
          whether a hire has earned it or tracks payout (see the type comment
          above). */}
      {!!campaign.bonus_amount_cents && !!campaign.bonus_gmv_threshold_cents && (
        <div className="rounded-xl border border-amber-200 bg-gradient-to-b from-amber-50/70 to-transparent p-4 space-y-3 dark:border-amber-900/40 dark:from-amber-950/20">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-500 shadow-sm shadow-amber-500/40">
              <Gift className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
            </span>
            <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-wide">
              Creator Bonus
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <div>
              <span className="text-muted-foreground">Bonus amount</span>{' '}
              <span className="font-semibold">{usd(campaign.bonus_amount_cents)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">GMV threshold</span>{' '}
              <span className="font-semibold">{usd(campaign.bonus_gmv_threshold_cents)}</span>
            </div>
          </div>
          {campaign.bonus_terms?.trim() && (
            <div className="pt-2 border-t border-amber-200/60 dark:border-amber-900/40">
              <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-1">
                Bonus terms
              </p>
              <p className="text-sm text-foreground/80 whitespace-pre-wrap">{campaign.bonus_terms.trim()}</p>
            </div>
          )}
        </div>
      )}

      {/* Videos To Remake — the brief's worklist on a Viral Video Remake job.
          Deliberately gated on is_viral_remake: this page has never shown
          brief_video_urls (they live in the campaign sheet on the list page),
          and a standard campaign must keep rendering exactly as it does today.
          Read-only, so there is no edit control and nothing to audit-log.
          briefVideoEntries() absorbs null / non-array / entries with no url. */}
      {isViralRemake(campaign) && (() => {
        const remakeVideos = briefVideoEntries(campaign.brief_video_urls);
        if (remakeVideos.length === 0) return null;
        return (
          <div className="rounded-xl border border-sky-200 bg-gradient-to-b from-sky-50/70 to-transparent p-4 space-y-4 dark:border-sky-900/40 dark:from-sky-950/20">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-sky-400 to-blue-500 shadow-sm shadow-blue-500/40">
                <Repeat2 className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
              </span>
              <p className="text-xs font-semibold text-sky-800 dark:text-sky-300 uppercase tracking-wide">
                {VIDEOS_TO_REMAKE_HEADING}
              </p>
              <span className="ml-auto text-[11px] font-medium text-muted-foreground">
                {remakeVideos.length} video{remakeVideos.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {remakeVideos.map((entry, i) => {
                const description = remakeDescription(entry);
                return (
                  <div
                    key={`${entry.url}-${i}`}
                    className="rounded-lg border bg-card p-3 space-y-2 shadow-sm"
                  >
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-100 text-[10px] font-bold text-sky-700 dark:bg-sky-900/50 dark:text-sky-300">
                        {i + 1}
                      </span>
                      <p className="text-sm font-medium leading-tight">{entry.name || `Video ${i + 1}`}</p>
                    </div>
                    <a
                      href={entry.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-start gap-1.5 text-blue-600 hover:underline text-xs pl-7"
                    >
                      <ExternalLink className="h-3 w-3 shrink-0 mt-0.5" />
                      <span className="break-all">{entry.url}</span>
                    </a>
                    {/* Soft all-around border + an icon on the label, not a
                        left accent stripe — a stripe reads as an alert
                        callout, and this is an instruction, not a warning. */}
                    {description && (
                      <div className="ml-7 rounded-md border border-sky-200 bg-sky-50/70 px-2.5 py-2 dark:border-sky-900/40 dark:bg-sky-950/30">
                        <p className="flex items-center gap-1 text-[10px] font-semibold text-sky-700 dark:text-sky-400 uppercase tracking-wide mb-0.5">
                          <PenLine className="h-2.5 w-2.5" />
                          {WHAT_TO_REMAKE_LABEL}
                        </p>
                        <p className="text-sm text-foreground/90 whitespace-pre-wrap break-words">{description}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Hires table */}
      <div className="rounded-lg border bg-card">
        <div className="flex items-center gap-1 border-b px-4 pt-1">
          {([
            { key: 'hired' as HireTab,        label: 'Hired',        count: hiredCount },
            { key: 'invited' as HireTab,      label: 'Invites',      count: invitedCount },
            { key: 'waitlisted' as HireTab,   label: 'Waitlisted',   count: waitlistedCount },
            { key: 'rejected' as HireTab,     label: 'Rejected',     count: rejectedCount },
            { key: 'applications' as HireTab, label: 'Applications', count: applications.length },
            { key: 'videos' as HireTab,       label: 'Videos',       count: videoStats?.total ?? 0 },
          ]).map(({ key, label, count }) => (
            <button
              key={key}
              type="button"
              onClick={() => { setHireTab(key); if (key === 'applications') setAppSubTab('pending'); }}
              className={`px-3 py-2.5 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                hireTab === key
                  ? 'border-b-2 border-foreground text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
              <span className={`text-xs rounded-full px-1.5 py-0.5 tabular-nums ${hireTab === key ? 'bg-foreground/10 text-foreground' : 'bg-muted text-muted-foreground'}`}>
                {count}
              </span>
            </button>
          ))}
        </div>

        {hireTab === 'videos' ? (
          <div>
            {/* Videos search + summary bar */}
            <div className="flex items-center justify-between gap-4 px-4 py-3 border-b flex-wrap">
              <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                <span>
                  <strong className="text-foreground">{videoStats?.total ?? 0}</strong> video{(videoStats?.total ?? 0) !== 1 ? 's' : ''}
                </span>
                {(videoStats?.total ?? 0) > 0 && (
                  <>
                    <span className="text-muted-foreground/40">·</span>
                    <span className="text-emerald-600 font-medium">
                      ${((videoStats?.totalGmvCents ?? 0) / 100).toLocaleString()} GMV
                    </span>
                    <span className="text-muted-foreground/40">·</span>
                    <span>{(videoStats?.totalViews ?? 0).toLocaleString()} views</span>
                  </>
                )}
                {videoDebouncedSearch && (
                  <>
                    <span className="text-muted-foreground/40">·</span>
                    <span>
                      <strong className="text-foreground">{videosPage?.filteredCount ?? 0}</strong> matching
                    </span>
                  </>
                )}
              </div>
              <Input
                placeholder="Search creator or video ID..."
                value={videoSearchInput}
                onChange={e => setVideoSearchInput(e.target.value)}
                className="h-8 text-sm w-56"
              />
            </div>

            {videosLoading ? (
              <div className="p-8 flex justify-center text-muted-foreground text-sm animate-pulse">Loading videos...</div>
            ) : (videoStats?.total ?? 0) === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground text-sm gap-2">
                <Video className="h-10 w-10 opacity-25 mb-2" />
                <p className="font-medium">No videos submitted yet.</p>
                <p className="text-xs">Videos appear here once creators submit via the brand portal.</p>
              </div>
            ) : (
              <div>
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-8 pl-6">#</TableHead>
                      <TableHead>Creator</TableHead>
                      <TableHead>Video ID</TableHead>
                      <TableHead className="text-right">GMV</TableHead>
                      <TableHead className="text-right">Views</TableHead>
                      <TableHead className="text-right">Orders</TableHead>
                      <TableHead className="text-right">Items Sold</TableHead>
                      <TableHead className="text-right">Submitted</TableHead>
                      <TableHead className="text-right pr-6">Link</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(videosPage?.rows ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-10">
                          No videos matching &ldquo;{videoSearchInput}&rdquo;
                        </TableCell>
                      </TableRow>
                    ) : (videosPage?.rows ?? []).map((v, i) => (
                      <TableRow key={v.id}>
                        <TableCell className="pl-6 text-xs text-muted-foreground">
                          {(videoPage - 1) * videoPageSize + i + 1}
                        </TableCell>
                        <TableCell className="py-3">
                          {v.roster_creators?.name && (
                            <p className="text-sm font-medium leading-tight">{v.roster_creators.name}</p>
                          )}
                          {v.roster_creators?.tiktok_username && (
                            <a
                              href={`https://www.tiktok.com/@${v.roster_creators.tiktok_username}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                            >
                              @{v.roster_creators.tiktok_username}
                            </a>
                          )}
                        </TableCell>
                        <TableCell className="py-3">
                          <p className="text-xs text-muted-foreground font-mono">
                            {v.tiktok_video_id ?? v.tiktok_video_url.split('/').pop() ?? '—'}
                          </p>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm font-semibold text-emerald-600">
                            ${((v.gmv_cents ?? 0) / 100).toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {v.views != null ? v.views.toLocaleString() : '—'}
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {v.orders != null ? v.orders.toLocaleString() : '—'}
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {v.units_sold != null ? v.units_sold.toLocaleString() : '—'}
                        </TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground whitespace-nowrap">
                          {v.submitted_at ? format(new Date(v.submitted_at), 'MMM d, yyyy') : '—'}
                        </TableCell>
                        <TableCell className="text-right pr-6">
                          <a
                            href={v.tiktok_video_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            Watch <ExternalLink className="h-3 w-3" />
                          </a>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <DataPagination
                  currentPage={videoPage}
                  totalPages={totalVideoPages}
                  pageSize={videoPageSize}
                  totalItems={videoDebouncedSearch ? (videosPage?.filteredCount ?? 0) : (videoStats?.total ?? 0)}
                  onPageChange={setVideoPage}
                  onPageSizeChange={(size) => { setVideoPageSize(size); setVideoPage(1); }}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Search bar for hire / application tabs */}
            <div className="flex items-center justify-end px-4 py-3 border-b">
              <Input
                placeholder="Search by name, @handle, email..."
                value={hireSearchInput}
                onChange={e => setHireSearchInput(e.target.value)}
                className="h-8 text-sm w-64"
              />
            </div>

            {hireTab === 'applications' && (
              <div className="flex items-center gap-1 border-b px-4">
                {(['pending', 'approved', 'rejected', 'completed', 'withdrawn'] as const).map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setAppSubTab(s)}
                    className={`px-3 py-2 text-xs font-medium transition-colors flex items-center gap-1.5 capitalize ${
                      appSubTab === s
                        ? 'border-b-2 border-foreground text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {s}
                    <span className={`text-xs rounded-full px-1.5 py-0.5 tabular-nums ${appSubTab === s ? 'bg-foreground/10 text-foreground' : 'bg-muted text-muted-foreground'}`}>
                      {appSubCounts[s]}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {(hireTab === 'applications' ? applicationsLoading : hiresLoading) ? (
              <div className="p-8 flex justify-center text-muted-foreground text-sm animate-pulse">Loading...</div>
            ) : hireTab === 'applications' ? (
          activeAppRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground text-sm gap-2">
              <Users className="h-10 w-10 opacity-25 mb-2" />
              <p className="font-medium">No {appSubTab} applications yet.</p>
            </div>
          ) : (
            <div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Creator</TableHead>
                    <TableHead>Proposed Packages</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Negotiation</TableHead>
                    <TableHead>Rejection Reason</TableHead>
                    <TableHead>Applied</TableHead>
                    <TableHead>Last Applied</TableHead>
                    <TableHead className="text-center">Rejected Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeAppRows.map(a => (
                    <TableRow key={a.id}>
                      <TableCell>
                        <div className="font-medium">{a.roster_creators?.name ?? '—'}</div>
                        {a.roster_creators?.tiktok_username && <div className="text-xs text-muted-foreground">@{a.roster_creators.tiktok_username}</div>}
                        {a.roster_creators?.email && <div className="text-xs text-muted-foreground">{a.roster_creators.email}</div>}
                      </TableCell>
                      <TableCell>
                        {(a.packages ?? []).length === 0 ? (
                          <span className="text-muted-foreground text-xs italic">—</span>
                        ) : (
                          <div className="space-y-1">
                            {(a.packages ?? []).map((pkg, i) => (
                              <div key={i} className="text-sm flex items-center gap-2">
                                <span className="font-medium tabular-nums">${(pkg.proposed_rate_cents / 100).toLocaleString()}</span>
                                <span className="text-muted-foreground text-xs">· {pkg.package_size} videos</span>
                                {pkg.proposed_rate_with_rights_cents && (
                                  <span className="text-muted-foreground text-xs">· w/ rights ${(pkg.proposed_rate_with_rights_cents / 100).toLocaleString()}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[200px] text-sm text-muted-foreground">
                        {a.note_to_brand ?? <span className="italic">—</span>}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          a.status === 'approved'  ? 'bg-green-100 text-green-800 border border-green-200' :
                          a.status === 'rejected'  ? 'bg-red-100 text-red-700 border border-red-200' :
                          a.status === 'withdrawn' ? 'bg-gray-100 text-gray-600 border border-gray-200' :
                          'bg-yellow-100 text-yellow-800 border border-yellow-200'
                        }>
                          {a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <NegotiationBadge
                          chain={chainByApplication.get(a.id) ?? []}
                          onOpen={() => setNegotiationDialog({
                            creatorName: a.roster_creators?.name ?? 'Creator',
                            brandName: campaign.brand_configs?.brand_name ?? 'Brand',
                            kind: 'application',
                            chain: chainByApplication.get(a.id) ?? [],
                          })}
                        />
                      </TableCell>
                      <TableCell className="max-w-[200px] text-sm">
                        {a.reject_reason
                          ? <span className="text-red-600">{a.reject_reason}</span>
                          : <span className="text-muted-foreground italic">—</span>
                        }
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {format(new Date(a.applied_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {a.last_applied_at ? format(new Date(a.last_applied_at), 'MMM d, yyyy') : '—'}
                      </TableCell>
                      <TableCell className="text-center tabular-nums">
                        {a.application_rejected_count ?? 0}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )
        ) : activeHireRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground text-sm gap-2">
            <Users className="h-10 w-10 opacity-25 mb-2" />
            <p className="font-medium">
              {hireTab === 'all'       ? 'No hires in this campaign yet.' :
               hireTab === 'hired'     ? 'No confirmed hires yet.' :
               hireTab === 'invited'   ? 'No pending invites.' :
               hireTab === 'rejected'  ? 'No rejected invites.' :
                                         'No waitlisted creators.'}
            </p>
            {hireTab === 'all' && (
              <p>Send offers from the <Link to="/users" className="text-pink-500 hover:underline">Creators page</Link>.</p>
            )}
          </div>
        ) : (
          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Creator</TableHead>
                  <TableHead>Status</TableHead>
                  {(hireTab === 'hired' || hireTab === 'all') && <TableHead>Source</TableHead>}
                  <TableHead className="text-right">Package</TableHead>
                  <TableHead className="text-right">Videos</TableHead>
                  <TableHead className="text-right">Payout</TableHead>
                  {hireTab === 'hired' && <TableHead className="text-right">Platform Fee</TableHead>}
                  <TableHead>Sample</TableHead>
                  {/* Also shown on 'hired' — an invite that negotiated a rate
                      still carries that history after converting to a hire;
                      hiding the column there made it look like the
                      negotiation never happened. */}
                  {(hireTab === 'invited' || hireTab === 'hired' || hireTab === 'all') && <TableHead>Negotiation</TableHead>}
                  {hireTab === 'invited' && <TableHead className="text-center">Reinvites</TableHead>}
                  {hireTab === 'rejected' && <TableHead>Declined At</TableHead>}
                  {hireTab === 'rejected' && <TableHead>Decline Reason</TableHead>}
                  <TableHead>{hireTab === 'invited' || hireTab === 'rejected' ? 'Last Invited At' : 'Date'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeHireRows.map(h => (
                  <TableRow key={h.id}>
                    <TableCell>
                      <div className="font-medium">{creatorName(h)}</div>
                      {creatorHandle(h) && <div className="text-xs text-muted-foreground">@{creatorHandle(h)}</div>}
                      {creatorEmail(h) && <div className="text-xs text-muted-foreground">{creatorEmail(h)}</div>}
                    </TableCell>
                    <TableCell><HireStatusBadge status={h.status} /></TableCell>
                    {(hireTab === 'hired' || hireTab === 'all') && (
                      <TableCell>
                        {h.source === 'application'
                          ? <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700">Application</span>
                          : h.source === 'invitation'
                          ? <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700">Invite</span>
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                    )}
                    <TableCell className="text-right tabular-nums text-sm">{h.package_size} videos</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {videosByHire[h.id] ?? 0}
                      <span className="text-muted-foreground text-xs"> / {h.videos_required}</span>
                      {(deletedByHire[h.id] ?? 0) > 0 && (
                        <div className="text-[10px] font-medium text-muted-foreground">
                          {deletedByHire[h.id]} removed
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      ${(h.creator_rate_cents / 100).toLocaleString()}
                    </TableCell>
                    {hireTab === 'hired' && (
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        ${(h.bmc_fee_cents / 100).toLocaleString()}
                      </TableCell>
                    )}
                    <TableCell>
                      <SampleStatusBadge status={h.sample_status} />
                      {h.sample_tracking_number && (
                        <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                          {h.sample_tracking_number}
                        </div>
                      )}
                      {receiptsByHire[h.id] && (
                        <div className="mt-1"><SampleReceiptBadge receipt={receiptsByHire[h.id]} live={h.status === 'confirmed' && !h.settled_at} /></div>
                      )}
                    </TableCell>
                    {(hireTab === 'invited' || hireTab === 'hired' || hireTab === 'all') && (
                      <TableCell>
                        <NegotiationBadge
                          chain={chainByHire.get(h.id) ?? []}
                          onOpen={() => setNegotiationDialog({
                            creatorName: creatorName(h),
                            brandName: campaign.brand_configs?.brand_name ?? 'Brand',
                            kind: 'invite',
                            chain: chainByHire.get(h.id) ?? [],
                          })}
                        />
                      </TableCell>
                    )}
                    {hireTab === 'invited' && (
                      <TableCell className="text-center tabular-nums text-sm">
                        {h.reinvited_count ?? 0}
                      </TableCell>
                    )}
                    {hireTab === 'rejected' && (
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {h.declined_at ? format(new Date(h.declined_at), 'MMM d, yyyy') : '—'}
                      </TableCell>
                    )}
                    {hireTab === 'rejected' && (
                      <TableCell className="text-sm max-w-[200px]">
                        {h.decline_reason
                          ? <span className="text-red-500 font-medium">{h.decline_reason}</span>
                          : <span className="italic text-muted-foreground">—</span>}
                      </TableCell>
                    )}
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {(hireTab === 'invited' || hireTab === 'rejected')
                        ? (h.last_invited_at ? format(new Date(h.last_invited_at), 'MMM d, yyyy') : '—')
                        : format(new Date(h.invited_at), 'MMM d, yyyy')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
            )}
          </>
        )}
      </div>

      {/* Edit TAP Link dialog */}
      <Dialog open={editTapOpen} onOpenChange={(open) => { if (!open) { setEditTapOpen(false); setTapLinkInput(''); } }}>
        <DialogContent className="sm:max-w-md overflow-hidden">
          <DialogHeader>
            <DialogTitle>{campaign.tap_affiliate_link ? 'Edit TAP Link' : 'Add TAP Link'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground break-all">{campaignName}</p>
            <div className="space-y-1.5">
              <Label htmlFor="tap-link-detail">TAP Link</Label>
              <Input
                id="tap-link-detail"
                placeholder="https://affiliate.tiktok.com/..."
                value={tapLinkInput}
                onChange={e => setTapLinkInput(e.target.value)}
                className={!isValidUrl(tapLinkInput) ? 'border-red-500' : ''}
              />
              {!isValidUrl(tapLinkInput) && <p className="text-xs text-red-500">Please enter a valid URL</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditTapOpen(false); setTapLinkInput(''); }} disabled={saveTapMutation.isPending}>Cancel</Button>
            <Button
              onClick={() => saveTapMutation.mutate(tapLinkInput)}
              disabled={saveTapMutation.isPending || !isValidUrl(tapLinkInput)}
            >
              {saveTapMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit budget cap dialog */}
      <Dialog open={editBudgetOpen} onOpenChange={(open) => { if (!open) { setEditBudgetOpen(false); setBudgetInput(''); } }}>
        <DialogContent className="sm:max-w-sm overflow-hidden">
          <DialogHeader>
            <DialogTitle>Edit Budget Cap</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="budget-cap">Budget Cap ($)</Label>
              <Input
                id="budget-cap"
                type="number"
                min="0"
                placeholder="e.g. 1000"
                value={budgetInput}
                onChange={e => setBudgetInput(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditBudgetOpen(false); setBudgetInput(''); }} disabled={saveBudgetMutation.isPending}>Cancel</Button>
            <Button
              onClick={() => saveBudgetMutation.mutate(Math.round(Number(budgetInput) * 100))}
              disabled={saveBudgetMutation.isPending || !budgetInput || isNaN(Number(budgetInput))}
            >
              {saveBudgetMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status confirm dialog */}
      {statusConfirm && (() => {
        const labels: Record<CampaignStatus, { title: string; desc: string; btnLabel: string; btnClass: string }> = {
          active:   { title: 'Activate Campaign',  desc: `Activate "${campaignName}"? Make sure the TAP affiliate link is correct before proceeding.`, btnLabel: 'Activate', btnClass: 'bg-green-600 hover:bg-green-700' },
          paused:   { title: 'Pause Campaign',     desc: `Pause "${campaignName}"? It will be hidden from creators until reactivated.`, btnLabel: 'Pause', btnClass: 'bg-orange-500 hover:bg-orange-600' },
          archived: { title: 'Archive Campaign',   desc: `Archive "${campaignName}"? Archived campaigns are removed from all active views.`, btnLabel: 'Archive', btnClass: 'bg-destructive hover:bg-destructive/90' },
          pending:  { title: 'Set to Pending',     desc: `Move "${campaignName}" back to pending?`, btnLabel: 'Confirm', btnClass: '' },
        };
        const meta = labels[statusConfirm];
        return (
          <Dialog open onOpenChange={(open) => { if (!open && !statusMutation.isPending) setStatusConfirm(null); }}>
            <DialogContent className="sm:max-w-md overflow-hidden">
              <DialogHeader>
                <DialogTitle className="break-all">{meta.title}</DialogTitle>
                <DialogDescription className="pt-1 break-all overflow-wrap-anywhere">{meta.desc}</DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2 pt-2">
                <Button variant="outline" onClick={() => setStatusConfirm(null)} disabled={statusMutation.isPending}>Cancel</Button>
                <Button className={meta.btnClass} disabled={statusMutation.isPending} onClick={() => statusMutation.mutate(statusConfirm)}>
                  {statusMutation.isPending ? 'Saving...' : meta.btnLabel}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}

      <NegotiationHistoryDialog
        state={negotiationDialog}
        onClose={() => setNegotiationDialog(null)}
      />
    </div>
  );
}
